// Webpostman tabanlı REST API sağlayıcı fabrikası.
// FEST Danışmanlık ve Deposel gibi aracı kargo firmaları AYNI Webpostman altyapısını kullanır;
// tek fark taban URL'dir (kargo firması/şube kodları cari başına UI'dan girilir). Bu fabrika,
// verilen taban URL ile tam bir CargoProvider üretir. Sağlayıcıya özgü mantık burada tektir.
//
// Not: İstek gövdesi application/x-www-form-urlencoded'dir (JSON değil).
// Not: User-Agent header'ı ZORUNLUdur — göndermeyen istekler firewall tarafından atak sayılıp bloklanır.
import type {
  CargoBrokerType,
  CargoCreds,
  CargoProvider,
  ConsignmentInput,
  ConsignmentResult,
  ShipmentListQuery,
  ShipmentListResult,
  TrackingRecord,
} from './types'
import { FEST_STATUS, normalizeFestStatusCode } from './types'
import { stripLeadingZeros } from './barcode'
import { formatWebpostmanDate } from './shipment'

const USER_AGENT = 'OPOP-Cargo/1.0 (+https://opop.panel)'
// Her istek için üst zaman sınırı. Timeout/AbortController YOKKEN tek asılı bir kargo API çağrısı
// (ör. karşı uç yanıt vermiyor) tüm gönderim isteğini undici varsayılanına (~300s) kadar kilitliyor,
// bu da gateway/Cloudflare (100s) timeout'una ve tarayıcıda "Load failed"e yol açıyordu. Sınırlı süre
// ile asılı çağrı fail-fast olur; sipariş başına net "zaman aşımı" hatası döner, batch akışta kalmaz.
const REQUEST_TIMEOUT_MS = 20_000
// Geçici sunucu hatası (503) için tekrar deneme. /consignment/add idempotenttir (aynı barkod → kod 103
// → başarı) → tekrar denemek güvenli. Toplam süre gateway (Cloudflare 100s) sınırını aşmasın diye
// deneme sayısı ve bekleme ÜST sınırlı.
// 429 BURADA DEĞİL — tekrar DENENMEZ: Deposel'e göre 429'dan sonra ısrar = IP kara liste
// (2026-09-30: 429 retry'ları + otomatik devam eden parçalar ~200 ek istek attı → ban). 429 bir DUR
// sinyalidir: host'a bekleme süresi (cooldown) başlatılır, istek CargoRateLimitedError ile biter.
const RETRY_STATUSES = new Set([503])
// Retry sayısı/beklemesi bilinçli DÜŞÜK tutulur: uzun backoff çağıran route'un zaman bütçesini boşa
// harcar. Kısa retry geçici hatayı toparlar; kalıcı hatada hızlı başarısız olup route'un bir sonraki
// siparişe geçmesine (ve kalanları `remaining` ile bir sonraki tura bırakmasına) izin verir.
const MAX_RETRIES = 2
const RETRY_BASE_MS = 700
const RETRY_MAX_WAIT_MS = 4_000
// Webpostman /tracking tek istekte en fazla 20 barkod işler (fazlası sessizce düşer/null döner).
const TRACKING_CHUNK = 20
// Gönderi listesi (/cargo) yanıtı büyüktür (Deposel: ~1.000 satır ≈ 1,5 MB, ~6 sn) → ayrı, uzun zaman sınırı.
const LISTING_TIMEOUT_MS = 60_000
// barkod= ile bulunamayan (sipno alanına yazılmış) gönderiler için tarih aralığı fallback penceresi.
// /tracking `barkod=` sorgusu yalnız musteribarkod'a göre eşler; sipno'ya yazılmış barkodları HİÇ
// döndürmez. Yalnız alim tarihi aralığı sorgusu tüm gönderileri getirir → geriye dönük 120 gün.
const DATE_RANGE_LOOKBACK_MS = 120 * 24 * 60 * 60 * 1000

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── KÜRESEL HIZ SINIRI KANCASI ──
// Kargo firması hız sınırını çıkış IP'si başına uygular (ör. Deposel ~20/s; aşımda 429, ısrarda IP
// ban). admin (send + durum yenileme) ve web (takip cron'u) AYNI IP'den bu API'yi vurur; her app kendi
// içinde sınırlı olsa da agregat hız sınırı aşabilir. Bu paket saf/DB-bağımsız kalsın diye asıl
// limiter (Postgres token-bucket) uygulama tarafından `setCargoRateLimiter` ile bağlanır ve HER HTTP
// isteğinden ÖNCE çağrılır (chunk'lı /tracking istekleri dahil). Bağlı değilse (ör. testler) sınır
// uygulanmaz.
export type CargoRateLimiter = (host: string) => Promise<void>
/** 429 alınınca çağrılır: süreçler-arası bekleme başlatır, etkin bekleme süresini (ms) döndürür. */
export type CargoRateLimitReporter = (host: string, retryAfterMs: number | null) => Promise<number | void>
let cargoRateLimiter: CargoRateLimiter | null = null
let cargoRateLimitReporter: CargoRateLimitReporter | null = null
/**
 * Uygulama, kargo API isteklerine küresel hız sınırı uygulamak için limiter'ı (ve 429 bildiricisini)
 * bağlar (idempotent).
 */
export function setCargoRateLimiter(
  limiter: CargoRateLimiter | null,
  reporter: CargoRateLimitReporter | null = null,
): void {
  cargoRateLimiter = limiter
  cargoRateLimitReporter = reporter
}

// ── 429 DEVRE KESİCİ ──
// 429 alınan host SÜREÇ İÇİNDE de hemen bloklanır: aynı turda uçuşta/kuyrukta olan istekler (paralel
// gönderim, chunk'lı takip) DB bildirimi yetişmeden fetch atmasın. Süreçler-arası blok DB'dedir
// (reporter + limiter). Reporter yoksa/başarısızsa bu süre tek koruma olur → varsayılan uzun tutulur.
const DEFAULT_RATE_LIMIT_COOLDOWN_MS = 10 * 60_000
const blockedHostsUntil = new Map<string, number>()

/** Kargo firması hız sınırı uyguladı (429) ya da host bekleme süresinde — istek atılmadı/atılmamalı. */
export class CargoRateLimitedError extends Error {
  readonly code = 'CARGO_RATE_LIMITED'
  readonly status = 429
  constructor(readonly retryAfterMs: number, readonly data?: unknown) {
    super(
      `Kargo firması hız sınırı uyguladı (HTTP 429). IP engeli riskine karşı bu firmaya istekler ` +
        `~${Math.max(1, Math.ceil(retryAfterMs / 60_000))} dk durduruldu; sonra tekrar deneyin.`,
    )
    this.name = 'CargoRateLimitedError'
  }
}

/** 429 / bekleme süresi hatası mı? (@repo/db'nin CargoRateLimitCooldownError'ı da aynı `code`u taşır.) */
export function isCargoRateLimitedError(err: unknown): err is Error & { retryAfterMs?: number } {
  const e = err as { code?: unknown; status?: unknown } | null | undefined
  return e?.code === 'CARGO_RATE_LIMITED' || e?.status === 429
}

/** Süreç-içi bloğu ve bağlı kancaları sıfırlar (testler için). */
export function resetCargoRateLimitState(): void {
  blockedHostsUntil.clear()
  cargoRateLimiter = null
  cargoRateLimitReporter = null
}

function hostBlockedMs(host: string): number {
  const until = blockedHostsUntil.get(host)
  if (until == null) return 0
  const left = until - Date.now()
  if (left <= 0) {
    blockedHostsUntil.delete(host)
    return 0
  }
  return left
}

function blockHost(host: string, ms: number): void {
  const until = Date.now() + ms
  if ((blockedHostsUntil.get(host) ?? 0) < until) blockedHostsUntil.set(host, until)
}

/** 429 alındı: host'u süreç içinde blokla + süreçler-arası bekleme bildir. Etkin süreyi (ms) döndürür. */
async function noteRateLimited(host: string, retryAfterMs: number | null): Promise<number> {
  let cooldownMs = Math.max(DEFAULT_RATE_LIMIT_COOLDOWN_MS, retryAfterMs ?? 0)
  if (cargoRateLimitReporter) {
    try {
      const reported = await cargoRateLimitReporter(host, retryAfterMs)
      if (typeof reported === 'number' && reported > 0) cooldownMs = reported
    } catch {
      /* fail-open: süreç-içi blok yine korur */
    }
  }
  blockHost(host, cooldownMs)
  return cooldownMs
}

function authHeaders(creds: CargoCreds): Record<string, string> {
  return {
    Authorization: creds.apiKey,
    From: creds.username,
    'User-Agent': USER_AGENT,
  }
}

/** TL tutarını API'nin beklediği kuruş "#.00" biçimine çevirir. */
export function toAmountString(amountTl: number): string {
  return (Math.round(amountTl * 100) / 100).toFixed(2)
}

// Webpostman altyapısı (FEST/Deposel/Soyalp AYNI ilçe veritabanını paylaşır) bazı ilçeleri
// kanonik adıyla İÇERMEZ; kendi listesindeki yazımı ister. `/county/{il_id}` uçlarından
// doğrulandı — API eşlemesi büyük/küçük harf DUYARSIZ (kanonik adların çoğu doğrudan eşleşir),
// ama farklı YAZILAN ilçeler için API'nin tam yazımını göndermek gerekir.
// Anahtar = Türkçe harf katlanmış + yalnız harf/rakam (yazım/boşluk varyasyonlarını tolere eder).
//
// DİKKAT: Onikişubat için override YOK — üç broker de "ONİKİŞUBAT" içeriyor, kanonik "Onikişubat"
// zaten eşleşiyor. (Eski "12Şubat" eşlemesi YANLIŞTI; API'de "12Şubat" hiç yok, gönderimi bozuyordu.)
const WEBPOSTMAN_COUNTY_OVERRIDES: Record<string, string> = {
  // Samsun/Ondokuzmayıs → API listesinde yalnız "19 MAYIS" (boşluklu) var, "Ondokuzmayıs" yok.
  ondokuzmayis: '19 Mayıs',
}

/** İlçe adını override eşlemesi için sadeleştirir: tr küçük harf → Türkçe katlama → yalnız [a-z0-9]. */
function foldCountyKey(name: string): string {
  return name
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, '')
}

/** İlçe adını Webpostman'in kabul ettiği yazıma çevirir; eşleşme yoksa değeri değiştirmeden döndürür. */
export function normalizeCountyName(countyName: string): string {
  return WEBPOSTMAN_COUNTY_OVERRIDES[foldCountyKey(countyName)] ?? countyName
}

/** ConsignmentInput'u Webpostman `consignment/add` form alanlarına eşler. */
export function buildConsignmentForm(input: ConsignmentInput): URLSearchParams {
  const form = new URLSearchParams()
  // Zorunlu alanlar
  form.set('customer', input.customer)
  form.set('province_name', input.provinceName)
  form.set('county_name', normalizeCountyName(input.countyName))
  form.set('address', input.address)
  form.set('branch_code', input.branchCode)
  // Barkod sayısal saklanır; sıfırsız gönderince gönderdiğimiz = API'nin sakladığı olur
  // (takip/movements sorguları da aynı sıfırsız biçimle yapılır → uçtan uca tutarlı).
  form.set('barcode', stripLeadingZeros(input.barcode))
  // Opsiyonel alanlar
  if (input.district) form.set('district', input.district)
  if (input.telephone) form.set('telephone', input.telephone)
  if (input.amount != null) form.set('amount', toAmountString(input.amount))
  if (input.amountTypeId != null) form.set('amount_type_id', String(input.amountTypeId))
  if (input.consignmentTypeId != null) form.set('consignment_type_id', String(input.consignmentTypeId))
  if (input.summary) form.set('summary', input.summary)
  if (input.quantity != null) form.set('quantity', String(input.quantity))
  if (input.orderNumber) form.set('order_number', input.orderNumber)
  // Ağırlık "Kg" olarak gönderilir (dokümana göre; ör. 1 = 1 kg). Değer olduğu gibi serileşir.
  if (input.weight != null) form.set('weight', String(input.weight))
  if (input.totalBulk != null) form.set('total_bulk', input.totalBulk.toFixed(2))
  if (input.seller) form.set('seller', input.seller)
  if (input.taxNumber) form.set('tax_number', input.taxNumber)
  if (input.senderNote) form.set('sender_note', input.senderNote)
  if (input.sendSms != null) form.set('send_sms', input.sendSms ? '1' : '0')
  return form
}

/**
 * undici `fetch` ağ hatasında dış mesajı çoğu zaman çıplak "fetch failed"tir; asıl sebep (ECONNRESET,
 * "other side closed", ENOTFOUND, ETIMEDOUT …) `err.cause` içindedir. Operatör "fetch failed" yerine
 * gerçek nedeni görsün (sorun ağ mı, veri mi anlaşılsın) diye cause'u mesaja iliştiririz.
 */
function describeFetchError(err: unknown): string {
  const e = err as (Error & { cause?: unknown }) | undefined
  const base = e?.message || 'Kargo API bağlantı hatası'
  const cause = e?.cause as (Error & { code?: string }) | undefined
  const detail = cause?.code || cause?.message
  if (!detail) return base
  if (base.trim().toLowerCase() === 'fetch failed') return `Kargo API bağlantı hatası: ${detail}`
  return base.includes(detail) ? base : `${base} (${detail})`
}

/** Merkezi Webpostman istek sarmalayıcı. GET → query string, POST/PUT/DELETE → form body. */
async function webpostmanRequest(
  baseUrl: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  creds: CargoCreds,
  form?: URLSearchParams,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<any> {
  let url = `${baseUrl}${path}`
  const init: RequestInit = {
    method,
    headers: authHeaders(creds),
    cache: 'no-store',
  }
  if (method === 'GET') {
    if (form && [...form.keys()].length > 0) url += `?${form.toString()}`
  } else if (form) {
    ;(init.headers as Record<string, string>)['Content-Type'] = 'application/x-www-form-urlencoded'
    init.body = form.toString()
  }

  // Küresel hız sınırı için hedef host (FEST/Deposel/Soyalp farklı host → bağımsız bütçe).
  let host = ''
  try { host = new URL(baseUrl).host } catch { /* baseUrl geçersizse limiter atlanır */ }

  // Hız sınırı/geçici hatada birden çok deneme yapılır (aşağı bak). Her deneme kendi
  // AbortController/timeout'una tabidir.
  let lastRetryErr: (Error & { status?: number; data?: unknown }) | null = null
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    // 429 DEVRE KESİCİ (süreç içi): host bekleme süresindeyse istek atma.
    const blockedBefore = host ? hostBlockedMs(host) : 0
    if (blockedBefore > 0) throw new CargoRateLimitedError(blockedBefore)
    // KÜRESEL HIZ SINIRI: fetch'ten ÖNCE paylaşılan bir slot al (tüm çağıranlar — send, cron, durum
    // yenileme — aynı IP bütçesini paylaşır). Retry de gerçek bir istektir → her denemede yeniden alınır.
    // Bağlı değilse no-op; kuyruk dolarsa limiter fırlatır (çağıran net hataya çevirir). Host DB'de
    // bekleme süresindeyse limiter CARGO_RATE_LIMITED fırlatır → süreç içinde de bloklanır.
    if (cargoRateLimiter && host) {
      try {
        await cargoRateLimiter(host)
      } catch (err) {
        if (isCargoRateLimitedError(err)) {
          const ms = Number(err.retryAfterMs) > 0 ? Number(err.retryAfterMs) : DEFAULT_RATE_LIMIT_COOLDOWN_MS
          blockHost(host, ms)
          throw new CargoRateLimitedError(ms)
        }
        throw err
      }
      // Slot beklenirken aynı süreçte başka bir istek 429 almış olabilir → yine de atma.
      const blockedAfter = hostBlockedMs(host)
      if (blockedAfter > 0) throw new CargoRateLimitedError(blockedAfter)
    }
    // Zaman aşımı koruması: hem başlık hem gövde okuması AbortController süresine tabidir
    // (timer gövde de okunduktan sonra temizlenir), böylece yavaş/asılı gövde de kilitleyemez.
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    init.signal = controller.signal
    let text: string
    let res: Response
    try {
      res = await fetch(url, init)
      text = await res.text()
    } catch (err) {
      // HTTP yanıtı yok (gerçek ağ hatası veya zaman aşımı) → net mesajla yükselt; çağıran
      // (createConsignment/testConnection) bunu sipariş başına başarısızlığa çevirir.
      const aborted = (err as Error)?.name === 'AbortError'
      const netErr = new Error(
        aborted
          ? `Kargo API zaman aşımı (${timeoutMs / 1000}s içinde yanıt yok)`
          : describeFetchError(err),
      )
      // AĞ DÜZEYİ KOPMA (bayat keep-alive soketi / ECONNRESET / "other side closed") → undici bunu
      // çıplak "fetch failed" olarak fırlatır ve TEK istekte kalıcı DEĞİLDİR: FEST HTTP sunucusu
      // havuzdaki soketi kapatınca undici bayat soketi seçip resetleniyor; toplu gönderimde 5 eşzamanlı
      // istekten bir kısmı bu yüzden düşüyordu (429/503 gibi retry edilmeden). Yeni bağlantıyla tekrar
      // dene — /consignment/add idempotent (barkod → kod 103 → başarı) olduğundan güvenli; ilk deneme
      // sunucuya ulaşıp yanıt kaybolsa bile retry "zaten kayıtlı" alıp başarı döner (çift gönderim yok).
      // ZAMAN AŞIMI (abort) HARİÇ: onu tekrar denemek route'un dispatch zaman bütçesini tüketir → hızlı
      // başarısız ol (kalan siparişler `remaining` ile bir sonraki tura devreder).
      if (!aborted && attempt < MAX_RETRIES) {
        lastRetryErr = netErr as typeof lastRetryErr
        await sleep(retryWaitMs(null, attempt))
        continue
      }
      throw netErr
    } finally {
      clearTimeout(timer)
    }
    let data: any = null
    try {
      data = text ? JSON.parse(text) : null
    } catch {
      // Bazı uçlarda (bireysel takip) HTML döner; JSON değilse ham metni taşı.
      data = { _raw: text }
    }
    if (!res.ok) {
      // HIZ SINIRI (429): TEKRAR DENEME — host'u beklemeye al (süreç içi + tüm süreçler) ve dur.
      if (res.status === 429 && host) {
        const cooldownMs = await noteRateLimited(host, parseRetryAfterMs(res.headers.get('retry-after')))
        throw new CargoRateLimitedError(cooldownMs, data)
      }
      const msg = data?.result || data?.message || `Kargo API isteği başarısız (HTTP ${res.status})`
      const err = new Error(msg) as Error & { status?: number; data?: unknown }
      err.status = res.status
      err.data = data
      // Geçici hata (503): son deneme değilse backoff sonra tekrar dene.
      // Retry-After başlığı varsa ona uy (saniye ya da HTTP-date), yoksa üstel backoff + jitter;
      // her ikisi de üst sınırla kapaklanır (gateway timeout'unu aşmamak için).
      if (RETRY_STATUSES.has(res.status) && attempt < MAX_RETRIES) {
        lastRetryErr = err
        await sleep(retryWaitMs(res.headers.get('retry-after'), attempt))
        continue
      }
      throw err
    }
    return data
  }
  // Döngü yalnız retry ile biterse buraya düşülür (son denemede de retryable durum döndü).
  throw lastRetryErr ?? new Error('Kargo API isteği başarısız')
}

/** Belirtilen ms kadar bekler. */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Bir sonraki tekrar denemeden önce beklenecek süreyi hesaplar (ms).
 * Retry-After başlığı öncelikli (saniye tamsayısı ya da HTTP-date); yoksa üstel backoff
 * (RETRY_BASE_MS * 2^attempt) + rastgele jitter. Sonuç RETRY_MAX_WAIT_MS ile kapaklanır —
 * geçici hata (503) retry'ları batch'i gateway (Cloudflare 100s) sınırına kadar uzatmasın.
 */
export function retryWaitMs(retryAfter: string | null, attempt: number): number {
  const fromHeader = parseRetryAfterMs(retryAfter)
  if (fromHeader != null) return Math.min(fromHeader, RETRY_MAX_WAIT_MS)
  const backoff = RETRY_BASE_MS * 2 ** attempt
  const jitter = Math.random() * RETRY_BASE_MS
  return Math.min(backoff + jitter, RETRY_MAX_WAIT_MS)
}

/** Retry-After başlığını ms'ye çevirir (saniye tamsayısı ya da HTTP-date); yoksa/geçersizse null. */
export function parseRetryAfterMs(retryAfter: string | null): number | null {
  if (!retryAfter) return null
  const asSeconds = Number(retryAfter)
  if (Number.isFinite(asSeconds) && asSeconds >= 0) return asSeconds * 1000
  const asDate = Date.parse(retryAfter)
  if (Number.isFinite(asDate)) return Math.max(asDate - Date.now(), 0)
  return null
}

/** Webpostman bazı uçlarda (add/edit/delete) `error`'ı STRING ("true"/"false") döndürür — boolean
 *  gibi güvenle yorumla. Yalnız "true"/1 hata; "false"/0/boş/undefined başarı. */
function apiErrored(data: any): boolean {
  const e = data?.error
  if (e === true || e === 1) return true
  if (typeof e === 'string') { const v = e.trim().toLowerCase(); return v === 'true' || v === '1' }
  return false
}

/** `result`/`message` string VEYA dizi olabilir → tek metne indir. */
function apiMessage(data: any, fallback: string): string {
  const r = data?.result ?? data?.message
  if (Array.isArray(r)) { const j = r.filter(Boolean).map(String).join(' '); return j || fallback }
  return r != null && String(r) !== '' ? String(r) : fallback
}

/** "…nolu barkod daha önce kayıt edilmiş." (code 103) → gönderi zaten kayıtlı (idempotent başarı). */
function apiAlreadyRegistered(data: any): boolean {
  return String(data?.code ?? '') === '103' || /daha önce kay[ıi]t/i.test(apiMessage(data, ''))
}

/** Takip kaydını (data[] elemanı) TrackingRecord'a eşler. */
function mapTrackingRecord(row: any): TrackingRecord {
  const statusCode = normalizeFestStatusCode(String(row.statu_no ?? ''))
  return {
    gonderino: String(row.gonderino ?? ''),
    // Gerçek takip no = cikisno (taşıyıcı barkodu); yoksa tedbarkod. gonderino'ya DÜŞME
    // (o API iç numarası, müşteri onunla takip edemez).
    trackingNo: String(row.cikisno ?? row.tedbarkod ?? ''),
    // TUZAK: bazı cariler bizim barkodumuzu `musteribarkod` yerine `sipno` alanına yazar
    // (kargo firması Excel'den elle girerken). O durumda musteribarkod BOŞ döner; barkodumuzu
    // sipno'dan kurtarırız ki eşleştirme (cron + admin) yine tutsun. musteribarkod doluysa
    // (normal akış) sipno'ya HİÇ düşülmez. `||` kullanılır: boş string de fallback tetiklesin.
    musteribarkod: String(row.musteribarkod || row.sipno || ''),
    statusCode,
    // Toplu takipte `durum` sayısal kovadır (0-6); ince statu_no'dan ayrı taşınır.
    bucket: row.durum != null ? String(row.durum) : undefined,
    // Durum metni: normalde `sonuc` zengin metin verir ("Sevk Edildi", "Çıkış Şubesinde").
    // İSTİSNA: teslimde (statu_no 10) `sonuc` alanı teslim ALAN kişinin adını taşır (durum değil),
    // o yüzden kanonik "Teslim Edildi" gösterilir. `sonuc` boşsa (henüz hareketsizken)
    // FEST_STATUS metnine düşülür (`??` değil `||` — boş string de fallback tetiklesin).
    statusText: statusCode === '10'
      ? FEST_STATUS['10']
      : String(row.sonuc || FEST_STATUS[statusCode] || ''),
    sipno: row.sipno != null ? String(row.sipno) : undefined,
    // Gönderinin o an bulunduğu şube/dağıtım merkezi = `onsonuc` (ör. "BİNGÖL DTM").
    // DİKKAT: `sube` alanı gerçek konum DEĞİL, aracı hub'ıdır (ör. "SER GRUP GALAKSİ" / "ARAS SER
    // GET") ve tüm gönderilerde sabittir — kullanılmaz. `onsonuc` bazen randevu TARİHİ taşıyabilir
    // ("05-04-2021"); tarih desenini şube sanmayalım.
    currentBranch: pickCurrentBranch(row),
    currentBranchCode: row.subekod ? String(row.subekod) : undefined,
    raw: row,
  }
}

/** `onsonuc`'tan bulunduğu şube/DTM'yi çıkarır; tarih (randevu) veya boşsa undefined döner. */
function pickCurrentBranch(row: any): string | undefined {
  const os = row.onsonuc != null ? String(row.onsonuc).trim() : ''
  if (!os) return undefined
  if (/^\d{1,2}[-./]\d{1,2}[-./]\d{2,4}$/.test(os)) return undefined // randevu tarihi, şube değil
  return os
}

/** Tarihi Webpostman'in beklediği "GG-AA-YYYY" biçimine çevirir (yerel/TR saatiyle). */
function fmtFestDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`
}

/**
 * Alım tarihi aralığıyla hesabın TÜM gönderilerini çeker (durums=-1 → tümü). `barkod=` sorgusunun
 * aksine bu uç, barkodu `sipno` alanına yazılmış (musteribarkod boş) gönderileri de döndürür.
 * mapTrackingRecord musteribarkod boşsa sipno'dan barkodu kurtardığı için dönen kayıtlar yine
 * bizim barkodumuzla eşleşebilir.
 */
async function webpostmanTrackByDateRange(
  baseUrl: string,
  creds: CargoCreds,
  startDate: string,
  endDate: string,
): Promise<TrackingRecord[]> {
  const form = new URLSearchParams()
  form.set('alim_start', startDate)
  form.set('alim_end', endDate)
  form.set('durums', '-1')
  const data = await webpostmanRequest(baseUrl, 'POST', '/tracking', creds, form)
  const rows: any[] = Array.isArray(data?.data) ? data.data : []
  return rows.map(mapTrackingRecord)
}

/**
 * Verilen taban URL için tam bir Webpostman CargoProvider üretir.
 * FEST ve Deposel yalnız `baseUrl` (ve `type` etiketi) ile ayrışır; iş mantığı ortaktır.
 */
export function createWebpostmanProvider(type: CargoBrokerType, baseUrl: string): CargoProvider {
  return {
    type,

    async createConsignment(input: ConsignmentInput, creds: CargoCreds): Promise<ConsignmentResult> {
      try {
        const data = await webpostmanRequest(baseUrl, 'POST', '/consignment/add', creds, buildConsignmentForm(input))
        const already = apiAlreadyRegistered(data)
        // Barkod zaten kayıtlıysa (idempotent) başarı say — gönderi mevcut, hedef sağlanmış.
        if (apiErrored(data) && !already) {
          return { success: false, barcode: null, recordId: null, message: apiMessage(data, 'Gönderi oluşturulamadı'), raw: data }
        }
        return {
          success: true,
          barcode: data?.barcode ? String(data.barcode) : null,
          recordId: data?.record_id ? String(data.record_id) : null,
          message: already ? 'Zaten kayıtlı (mevcut gönderi)' : apiMessage(data, 'Kayıt Başarılı'),
          raw: data,
        }
      } catch (err) {
        const e = err as Error & { data?: any; retryAfterMs?: number }
        if (isCargoRateLimitedError(err)) {
          // Çağıran (send route) bu firmaya kalan gönderimi durdurur — tekrar denemez.
          return {
            success: false, barcode: null, recordId: null, message: e.message, raw: e.data,
            rateLimited: true, retryAfterMs: e.retryAfterMs,
          }
        }
        return { success: false, barcode: null, recordId: null, message: e.message, raw: e.data }
      }
    },

    async trackByBarcodes(barcodes: string[], creds: CargoCreds): Promise<TrackingRecord[]> {
      if (barcodes.length === 0) return []
      // /tracking SORGU-eşleşmesinde barkodu OLDUĞU GİBİ arar (sıfır kırpmaz): API ile kaydedilen
      // gönderiler sıfırsız (`400003689`) saklanırken, panelden/Excel'den MANUEL yüklenenler 11 haneli
      // iç biçimle (`00400003689`) kaydedilmiş olabilir. Tek biçimle sorulursa diğer grup HİÇ eşleşmez
      // (Deposel'de doğrulandı: `barkod=400003689`→0 kayıt, `barkod=00400003689`→1 kayıt). Bu yüzden
      // her barkod HEM sıfırsız HEM 11 haneli sıfır-dolgulu biçimde sorulur; dönen kayıtlar aşağıda
      // (ve çağıran tarafta) stripLeadingZeros ile normalize edilerek tek biçime indirgenir.
      const variants = new Set<string>()
      for (const b of barcodes) {
        const stripped = stripLeadingZeros(b)
        variants.add(stripped)                    // API kayıtları (sayısal/sıfırsız)
        variants.add(stripped.padStart(11, '0'))  // manuel/Excel kayıtları (11 haneli iç biçim)
      }
      const all = [...variants]
      // /tracking en fazla ~20 barkod işler: 21-24'te sonucu 20 kayıtla sınırlar, 25+ barkodda
      // isteği tümden reddedip `null` döner (sessiz veri kaybı). Bu yüzden barkodlar ≤20'lik
      // parçalara bölünüp ayrı isteklerle sorgulanır, sonuçlar birleştirilir.
      const out: TrackingRecord[] = []
      for (let i = 0; i < all.length; i += TRACKING_CHUNK) {
        const slice = all.slice(i, i + TRACKING_CHUNK)
        const form = new URLSearchParams()
        form.set('barkod', slice.join(','))
        const data = await webpostmanRequest(baseUrl, 'POST', '/tracking', creds, form)
        const rows: any[] = Array.isArray(data?.data) ? data.data : []
        for (const row of rows) out.push(mapTrackingRecord(row))
      }

      // FALLBACK: barkod= sorgusu yalnız musteribarkod'a göre eşler; kargo firması barkodumuzu
      // `sipno` alanına yazmışsa (musteribarkod boş) o gönderiler HİÇ dönmez. Bulunamayanları
      // tarih aralığı sorgusundan tamamla — orada tüm gönderiler döner, mapTrackingRecord sipno'dan
      // barkodu kurtarır. Yalnız gerçekten eksik barkod varsa tek ek istek atılır (best-effort).
      const wanted = new Set(barcodes.map((b) => stripLeadingZeros(b)).filter(Boolean))
      const found = new Set(out.map((r) => stripLeadingZeros(r.musteribarkod)).filter(Boolean))
      const missing = new Set([...wanted].filter((b) => !found.has(b)))
      if (missing.size > 0) {
        try {
          const now = new Date()
          const start = new Date(now.getTime() - DATE_RANGE_LOOKBACK_MS)
          const range = await webpostmanTrackByDateRange(baseUrl, creds, fmtFestDate(start), fmtFestDate(now))
          for (const rec of range) {
            const key = stripLeadingZeros(rec.musteribarkod)
            if (key && missing.has(key)) {
              out.push(rec)
              missing.delete(key) // aynı barkod birden çok kez eklenmesin
            }
          }
        } catch {
          // Tarih aralığı fallback'i best-effort; hata olursa barkod= sonuçlarıyla devam edilir.
        }
      }
      return out
    },

    async listShipments(query: ShipmentListQuery, creds: CargoCreds): Promise<ShipmentListResult> {
      // /cargo (GET) — /tracking'in aksine `degisim_start/end` (son hareket tarihi) filtresini destekler:
      // "son senkrondan beri değişenler" tek istekte gelir (dokümanda FEST/Deposel/Soyalp için aynı).
      const params = new URLSearchParams()
      params.set('durums', String(query.durums ?? -1))
      if (query.alimStart) params.set('alim_start', formatWebpostmanDate(query.alimStart))
      if (query.alimEnd) params.set('alim_end', formatWebpostmanDate(query.alimEnd))
      if (query.degisimStart) params.set('degisim_start', formatWebpostmanDate(query.degisimStart))
      if (query.degisimEnd) params.set('degisim_end', formatWebpostmanDate(query.degisimEnd))
      const data = await webpostmanRequest(baseUrl, 'GET', '/cargo', creds, params, LISTING_TIMEOUT_MS)
      if (apiErrored(data)) {
        const err = new Error(apiMessage(data, `Gönderi listesi alınamadı (hata ${data?.hata ?? '?'})`)) as Error & { data?: unknown }
        err.data = data
        throw err
      }
      const rows: unknown[] = Array.isArray(data?.data) ? data.data : []
      const total = Number(data?.toplam_kayit)
      return { total: Number.isFinite(total) ? total : rows.length, rows }
    },

    async getMovements(barcode: string, creds: CargoCreds): Promise<TrackingRecord | null> {
      // Barkod sayısal saklanır → sıfırsız biçimle sorgulanır.
      const data = await webpostmanRequest(baseUrl, 'GET', `/movements/${encodeURIComponent(stripLeadingZeros(barcode))}`, creds)
      if (!data || apiErrored(data)) return null
      const statusCode = normalizeFestStatusCode(String(data.statu_no ?? ''))
      return {
        gonderino: String(data.barkod_no ?? data.gonderino ?? ''),
        // Movements ucunda gerçek takip no `cikis_no` (alt çizgili; tracking'teki `cikisno`dan farklı).
        trackingNo: String(data.cikis_no ?? data.tedbarkod ?? data.cikisno ?? ''),
        musteribarkod: String(data.gonderici_barkod ?? barcode),
        statusCode,
        // DİKKAT: `sonuc_durum` alanı sabit ETİKETtir ("SONUÇ"), durum değeri DEĞİL. Asıl durum
        // `sonuc_aciklama` ("Yolda"); yoksa statu_no haritasına düş. `durum` ("Normal") hareket tipidir.
        statusText: String(data.sonuc_aciklama ?? FEST_STATUS[statusCode] ?? ''),
        // Hareket dökümünde bulunduğu şube için güvenilir bir alan yok (`varis_subesi` aracı hub'ı).
        // Şube bilgisi trackByBarcodes (`onsonuc`) üzerinden alınır.
        raw: data,
      }
    },

    async testConnection(creds: CargoCreds): Promise<{ ok: boolean; message: string }> {
      // NOT: /province auth'u doğrulamaz (geçersiz anahtarla bile 200 döner). Kimlik doğrulaması
      // yapan /tracking kullanılır.
      //
      // ⚠️ HTTP DURUMU auth kararı için GÜVENİLMEZ: sağlayıcılar auth BAŞARISIZLIĞINI farklı
      // durumlarla verir — FEST 200 + code 179; Deposel 401 + code 179. Üstelik Deposel GEÇERLİ
      // anahtarla parametre eksik olduğunda da 401 döndürür ("En az bir parametre gönderilmelidir"),
      // ki bu auth'un GEÇTİĞİ anlamına gelir. Bu yüzden auth kararı YALNIZ code 179 / "erişim
      // reddedildi" imzasına göre verilir; "her 401 = kimlik hatası" varsayımı yanlış negatif üretir.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isAuthFailure = (d: any, msg: string): boolean =>
        d?.code === 179 || String(d?.code ?? '') === '179' ||
        /geçersiz api|erişim reddedildi|api anahtar/i.test(msg)
      try {
        // Sıfır olmayan sahte barkod: Deposel `barkod=0`'ı "parametre yok" sayıp 401 döner;
        // gerçek bir değer geçerli anahtarla temiz 200 verir (eşleşme yoksa boş data).
        const data = await webpostmanRequest(baseUrl, 'POST', '/tracking', creds, new URLSearchParams({ barkod: '1' }))
        if (isAuthFailure(data, String(data?.message ?? data?.result ?? ''))) {
          return { ok: false, message: data?.message || 'Kimlik doğrulama başarısız — kullanıcı adı/anahtar hatalı' }
        }
        return { ok: true, message: 'Bağlantı başarılı' }
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const e = err as Error & { status?: number; data?: any }
        const msg = String(e.data?.message ?? e.data?.result ?? e.message ?? '')
        if (isAuthFailure(e.data, msg)) {
          return { ok: false, message: e.data?.message || 'Kimlik doğrulama başarısız — kullanıcı adı/anahtar hatalı' }
        }
        // 429 / bekleme süresi: kimlik doğrulanamadı — "başarılı" DEME (aşağıdaki status kuralı 429'u
        // yanlışlıkla başarı sayardı).
        if (isCargoRateLimitedError(err)) return { ok: false, message: e.message }
        // HTTP yanıtı geldi ama auth hatası değil (ör. Deposel "parametre eksik" 401'i) → kimlik & bağlantı OK.
        if (e.status != null) return { ok: true, message: 'Bağlantı başarılı' }
        // Gerçek ağ/timeout hatası (HTTP yanıtı yok) → bağlantı kurulamadı.
        return { ok: false, message: e.message }
      }
    },

    async editConsignment(barcode: string, input: ConsignmentInput, creds: CargoCreds): Promise<ConsignmentResult> {
      try {
        const data = await webpostmanRequest(baseUrl, 'PUT', `/consignment/edit/${encodeURIComponent(stripLeadingZeros(barcode))}`, creds, buildConsignmentForm(input))
        if (apiErrored(data)) {
          return { success: false, barcode: null, recordId: null, message: apiMessage(data, 'Güncelleme başarısız'), raw: data }
        }
        return { success: true, barcode, recordId: data?.record_id ? String(data.record_id) : null, message: apiMessage(data, 'Güncellendi'), raw: data }
      } catch (err) {
        const e = err as Error & { data?: any }
        return { success: false, barcode: null, recordId: null, message: e.message, raw: e.data }
      }
    },

    async deleteConsignment(barcode: string, creds: CargoCreds): Promise<{ success: boolean; message: string }> {
      try {
        const data = await webpostmanRequest(baseUrl, 'DELETE', `/consignment/delete/${encodeURIComponent(stripLeadingZeros(barcode))}`, creds)
        return { success: !apiErrored(data), message: apiMessage(data, 'Silindi') }
      } catch (err) {
        return { success: false, message: (err as Error).message }
      }
    },
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
